/* eslint-disable no-unused-expressions,object-curly-newline,no-unused-vars */
/* global PLATFORM, G */
import { asyncPipeSpread, asyncPipeSpreadIf, bulk, getFirstItem, isArr, isObj, isStr, setKey } from 'lib/util'
import { empty } from 'lib/util/object'
import { get, set } from 'lib/sequence/component/state/value'
import { hide, show } from 'lib/sequence/component/state/hidden'
import sequenceComponentFind from 'lib/sequence/component/children/find'
import map from 'lib/sequence/model/api/map'
import asObject from 'lib/sequence/component/children/asObject'
import swapListSerialAndName from 'app/_shared/action/partial/swapListSerialAndName'
import listOrganisationTypes from 'app/_shared/events/collection/listOrganisationTypes'
import internalOrganisations from 'model/organisation/collection/internal'
import { enable } from 'lib/sequence/component/state/disabled'
import settings from '@tenant/settings'
import displayStatus from 'app/_shared/action/partial/display/status'
import displayFavoriteButtonsFromModel from 'app/_shared/action/partial/favorite/display'
import displayContainerWithFields from 'app/_shared/action/partial/display/containerWithFields'

const display = x => (get(x) ? show(x) : hide(x))
const bulkDisplay = bulk(display)
const bulkHide = bulk(hide)

/**
 * Checks if `model` has a ref `parent` that is an {@link internalOrganisations}.
 *
 * @param {Gaia.Model.Spec} model current module composition
 * @returns {*|boolean}
 */
// TODO: Refactor this after we added a flag server side instead of needing to check of ID
export const hasInternalParent = (model) => {
  const { trees = {} } = model[G.CACHE] || {}
  const { parent = [] } = trees
  const modelParents = getFirstItem(parent)

  return parent.length
    ? internalOrganisations.some(x => modelParents.includes(x.key))
    : false
}

/**
 * Checks if `model` has an internal parent and if that parent allows the organisation
 * to be published.
 *
 * @param {Gaia.Model.Spec} model
 * @returns {boolean|*}
 */
export const canBePublished = (model) => {
  const { trees = {} } = model[G.CACHE] || {}
  const { parent = [] } = trees
  const modelParents = getFirstItem(parent)

  const internalParent = parent.length
    ? internalOrganisations.find(x => modelParents.includes(x.key))
    : {}

  return internalParent?.canBePublished
}

/**
 * Maps various properties inside the property box.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const mapProperties = module => async (children, ...args) => {
  const model = module[G.MODEL]

  const bulkMap = bulk(map(model))

  if (PLATFORM === 'web') {
    const {
      header,
      contactChannels,
      organisationInfo,
      organisationHeader,
      organisationStatus,
    } = children

    bulkMap(header, contactChannels, organisationInfo, organisationHeader, organisationStatus)
  }

  if (PLATFORM === 'mobile') {
    const { info, statusContainer, details } = children
    bulkMap(info, statusContainer, details)
  }

  return [children, ...args]
}

/**
 * Presets `type`.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const presetType = module => component => async (children, ...args) => {
  const model = module[G.MODEL]

  const { type } = children

  const organisationType = getFirstItem(
    listOrganisationTypes(module, component, model[G.CHILDREN].type[G.CACHE]),
  )?.value

  set(type, organisationType)

  return [children, ...args]
}

/**
 * Presets `supportedBy`.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const presetSupportedBy = module => async (children, ...args) => {
  const context = module[G.ADAPTER][G.SESSION][G.STATE]?.[G.CONTEXT]
  // component
  const { supportedBy } = children

  if (settings.suppressSupportedBy && context !== 'adm') {
    set(supportedBy, null)
    hide(supportedBy)
  } else {
    // model
    const model = module[G.MODEL]
    const supportedByOrg = getFirstItem(model[G.CHILDREN].supportedBy?.[G.CACHE])?.value?.name

    supportedByOrg && set(supportedBy, supportedByOrg)
  }

  return [children, ...args]
}

/**
 * Presets `checkForDuplicates`.
 *
 * Determines if the user should check the org for duplicates. They should if the organisations
 * `duplicateOf` ref is empty or non-existent and the `toBeValidated` property is set.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*): function(*, ...[*]): Promise<*>}
 */
const presetDuplicateStatus = module => component => async (children, ...args) => {
  // model
  const model = module[G.MODEL]
  const modelCache = getFirstItem(model?.[G.CACHE]) || {}

  const { duplicateOf = null } = modelCache?.refs || {}
  const { toBeValidated = {} } = modelCache?.value || {}

  /**
   * The `duplicateOf` ref can have 3 values
   * - `undefined`: Has not been checked for duplicates
   * - `['ORG:XYZ']`: Has been checked and is duplicate
   * - `[]`: Has been checked and is not a duplicate
   */
  const isCheckedForDuplicates = duplicateOf && isArr(duplicateOf)
  const isDuplicate = isCheckedForDuplicates && duplicateOf.length
  const isDueValidation = toBeValidated && isObj(toBeValidated) && !empty(toBeValidated)
  const showDuplicateStatus = hasInternalParent(model) && !isCheckedForDuplicates

  // component
  const { checkForDuplicates } = children

  // eslint-disable-next-line no-nested-ternary
  const status = isDuplicate
    ? [{
      key: 'duplicate',
      icon: 'dot',
      color: 'warning',
      value: module[G.ADAPTER][G.INTL]._t(
        'label.duplicate',
        {
          ns: 'common',
          _key: 'label.duplicate',
          defaultValue: 'Duplicate',
        },
      ),
    }]
    : !isDuplicate && isDueValidation && showDuplicateStatus
      ? [{
        key: 'check_for_duplicates',
        icon: 'dot',
        color: 'warning',
        value: module[G.ADAPTER][G.INTL]._t(
          'label.checkForDuplicates',
          {
            ns: 'common',
            _key: 'label.checkForDuplicates',
            defaultValue: component[G.STATE].value,
          },
        ),
      }] : [{}]

  getFirstItem(status)?.key ? set(checkForDuplicates, status) : hide(checkForDuplicates)

  return [children, ...args]
}

/**
 * Presets `checkForDuplicates`.
 *
 * Determines if the user should check the org for duplicates. They should if the organisations
 * `duplicateOf` ref is empty or non-existent and the `toBeValidated` property is set.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
export const presetApprovalStatus = module => component => parent => async (children, ...args) => {
  // model
  const model = module[G.MODEL]
  const duplicateOf = getFirstItem(model[G.CHILDREN]?.duplicateOf?.[G.CACHE])

  // component
  const { approval } = children

  const status = !duplicateOf && canBePublished(model)
    ? [{
      key: 'to_be_approved',
      icon: 'dot',
      color: 'signal',
      value: module[G.ADAPTER][G.INTL]._t(
        'label.toBeApproved',
        {
          ns: 'common',
          _key: 'label.toBeApproved',
          defaultValue: component[G.STATE].value,
        },
      ),
    }]
    : [{}]

  getFirstItem(status)?.key ? set(approval, status) : hide(approval)

  return [children, ...args]
}

/**
 * Determines if the `btnApprove` should be shown.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayApprovalOptions = module => async (children, ...args) => {
  const model = module[G.MODEL]
  const modelCache = model[G.CACHE]

  const { duplicateOf = null } = modelCache?.refs || {}
  const {
    btnApprove,
    approveOriginal,
    approveDuplicate,
    checkDuplicate,
    repeatCheckDuplicate,
  } = children

  const orgParent = isStr(modelCache.refs.parent)
    ? modelCache.refs.parent
    : getFirstItem(modelCache.refs.parent)
  const orgParentKey = [orgParent?.key || orgParent]
  const isInternal = orgParentKey.includes('ORGANISATION:RA')

  // Checking if duplicate check has been performed
  if (duplicateOf && isArr(duplicateOf)) {
    duplicateOf.length === 0
    // is marked as 'not a duplicate'
      ? show(approveOriginal)
        && enable(approveOriginal)
        && hide(approveDuplicate)
    // is marked as a duplicate
      : show(approveDuplicate)
        && enable(approveDuplicate)
        && hide(approveOriginal)
    show(repeatCheckDuplicate)
    hide(checkDuplicate)
  } else {
    hide(approveOriginal)
    hide(approveDuplicate)
    hide(repeatCheckDuplicate)
    show(checkDuplicate)
  }

  isInternal
    ? show(btnApprove)
    : hide(btnApprove)

  return [children, ...args]
}

/**
 * Presets `parent` and `name`.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const presetParent = module => async (children, ...args) => {
  // model
  const model = module[G.MODEL]
  const parentCache = model[G.CHILDREN].parent?.[G.CACHE]

  // component
  const { parent, parentProperty } = children
  const parentOrg = getFirstItem(parentCache) || {}

  // only do this, if the parent is not ORGANISATION:RA
  if (parentCache && !hasInternalParent(model)) {
    const { name } = asObject(parent[G.CHILDREN])
    setKey(parentOrg?.key, 'key', parent[G.STATE])
    set(name, parentOrg?.value?.name)
  } else {
    hide(parentProperty)
  }

  return [children, ...args]
}

/**
 * Determines if `toBeValidated` and `validation` should be shown.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayToBeValidated = module => async (children, ...args) => {
  // model
  const model = module[G.MODEL]
  const validationCache = model[G.CHILDREN].toBeValidated?.[G.CACHE]
  const { reason = null } = validationCache || {}

  // component
  const { toBeValidated, validation } = children

  // If org has no data that needs to be validated, hide the status and text
  if (empty(reason)) hide(toBeValidated)
  if (empty(validationCache)) hide(validation)

  return [children, ...args]
}

/**
 * Determines if `supportUnit` should be shown.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displaySupportUnit = module => async (children, ...args) => {
  // model
  const model = module[G.MODEL]
  const isSupportUnit = model[G.CACHE]?.value?.support?.provides

  // component
  const { supportUnit } = children

  // Only show support unit batch if applicable
  isSupportUnit ? show(supportUnit) : hide(supportUnit)

  return [children, ...args]
}

/**
 * Determines if various properties inside the property box should be shown or not.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayProperties = module => async (children, ...args) => {
  // component
  const { number, phone, fax, email, website, supportedBy, summary } = children

  bulkDisplay(number, phone, fax, email, website, supportedBy, summary)

  settings.suppressOrganisationCustomerNumber
    && hide(number)

  return [children, ...args]
}

/**
 * Hides the ServiceItems section if the user doesn't have write permissions on the current
 * organisation.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayServiceItems = module => async (children, ...args) => {
  const model = module[G.MODEL]
  const { acl: modelAcl } = model[G.CACHE]
  const { serviceitem } = children

  try {
    module[G.ADAPTER][G.ACL].model([30], modelAcl)
  } catch (e) {
    hide(serviceitem)
  }

  return [children, ...args]
}

/**
 * Presets the organisation's address
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const presetAddress = module => async (children, ...args) => {
  const model = module[G.MODEL]
  const addressCache = model[G.CHILDREN].address[G.CACHE]
  const { [G.INTL]: intl } = module[G.ADAPTER]
  const { summary, address } = children

  if (PLATFORM === 'mobile') {
    addressCache && set(address, intl.address(addressCache, { inline: true }))
  }

  if (PLATFORM === 'web') {
    addressCache && set(summary, intl.address(addressCache, { inline: true }))
  }

  return [children, ...args]
}

/**
 * Displays the organisation's `number`
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayCustomerNumber = module => async (children, ...args) => {
  const { number, numberDivider } = children
  !get(number) && bulkHide(number, numberDivider)

  settings.suppressOrganisationCustomerNumber
    && hide(number)

  return [children, ...args]
}

/**
 * Organisation Action Detail
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*): function(...[*]): Promise<*[]>}
 */
export default module => component => async (...args) => asyncPipeSpread(
  displayFavoriteButtonsFromModel(module),
  mapProperties(module),
  presetSupportedBy(module),
  presetAddress(module),
  asyncPipeSpreadIf(() => PLATFORM === 'web')(
    presetType(module)(component),
    presetParent(module),
    presetDuplicateStatus(module)(component),
    presetApprovalStatus(module)(component)('parent'),
    displayToBeValidated(module),
    displaySupportUnit(module),
    displayProperties(module),
    displayServiceItems(module),
    displayApprovalOptions(module),
    swapListSerialAndName(module, 'ticket', 'list'),
  ),
  asyncPipeSpreadIf(() => PLATFORM === 'mobile')(
    displayCustomerNumber(module),
    displayContainerWithFields(
      ['detailsLabel', 'details'],
      [
        'number',
        { supportedBy: c => !getFirstItem(get(c)) },
      ],
    )(module),
  ),
  displayStatus()(module),
)(sequenceComponentFind(component), ...args)
